import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(response.data.valid);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('¡Contraseña actualizada!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al restablecer la contraseña';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#BA7B7C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#57534E]">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="invalid-token">
        <div className="w-full max-w-md px-6 py-16 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 
            className="text-3xl font-light tracking-tight text-[#1C1917] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Enlace inválido o expirado
          </h1>
          <p className="text-[#57534E] mb-8">
            Este enlace de recuperación ya no es válido. 
            Por favor solicita uno nuevo.
          </p>
          <Link 
            to="/forgot-password" 
            className="btn-primary inline-flex items-center space-x-2"
          >
            <span>Solicitar nuevo enlace</span>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="reset-success">
        <div className="w-full max-w-md px-6 py-16 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 
            className="text-3xl font-light tracking-tight text-[#1C1917] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            ¡Contraseña actualizada!
          </h1>
          <p className="text-[#57534E] mb-8">
            Tu contraseña ha sido restablecida exitosamente. 
            Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <Link 
            to="/login" 
            className="btn-primary inline-flex items-center space-x-2"
          >
            <span>Iniciar sesión</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center" data-testid="reset-password-page">
      <div className="w-full max-w-md px-6 py-16">
        <Link 
          to="/login" 
          className="inline-flex items-center space-x-2 text-sm text-[#57534E] hover:text-[#BA7B7C] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver al inicio de sesión</span>
        </Link>

        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#FDF8F6] rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[#BA7B7C]" />
          </div>
          <h1 
            className="text-3xl font-light tracking-tight text-[#1C1917] mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Nueva contraseña
          </h1>
          <p className="text-[#A8A29E]">
            Ingresa tu nueva contraseña
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
          <div>
            <label className="block text-sm text-[#57534E] mb-2">
              Nueva contraseña
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] focus:outline-none focus:border-[#BA7B7C] transition-colors pr-12"
                placeholder="••••••••"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#57534E]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-[#A8A29E] mt-1">Mínimo 6 caracteres</p>
          </div>

          <div>
            <label className="block text-sm text-[#57534E] mb-2">
              Confirmar contraseña
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#E7E5E4] focus:outline-none focus:border-[#BA7B7C] transition-colors pr-12"
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#57534E]"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
            data-testid="submit-btn"
          >
            {loading ? 'Guardando...' : 'Restablecer contraseña'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
